import React, { useCallback, useMemo } from 'react';
import { Box, Inscription } from '@common-fe/common-fe';

import { LockboxType, MatchedEmployee } from '@/modules/employer/components/Lockbox/lockbox.types';
import { MatchingListEmptyState } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import { useEmployeeCardsList } from '@/modules/employer/components/Lockbox/LockboxDetails/hooks';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

import EmployeesList from './components/EmployeesList';
import { addInvestmentsWarning } from './addInvestmentsWarning';

interface Props {
  matchedEmployees?: MatchedEmployee[];
}

const SuggestedEmployees: React.FC<Props> = ({ matchedEmployees }) => {
  const { editableLockbox, originalLockbox, setEditableLockbox, isEditMode } = useLockboxDetailsStore();

  const matchedEmployeesWithWarnings = useMemo(() => matchedEmployees
    ?.map((employee) => addInvestmentsWarning(employee, editableLockbox?.lockboxType === LockboxType.OUTBOUND_TOA)),
  [matchedEmployees, editableLockbox?.lockboxType]);

  const { fields } = useEmployeeCardsList({
    matchedEmployees: matchedEmployeesWithWarnings,
    lockboxType: editableLockbox?.lockboxType,
    ocrAddress: originalLockbox?.ocr?.details?.address,
  });

  const handleSelectEmployeeId = useCallback((employeeId?: string) => {
    const matchedEmployee = matchedEmployees
      ?.find((matchedEmployee) => matchedEmployee?.employeeId === employeeId);

    if (matchedEmployee) {
      setEditableLockbox({
        employeeId,
        accountId: matchedEmployee?.accountId,
        employeeAccountStatus: matchedEmployee?.accountStatus,
        employeeInvestmentsBalance: matchedEmployee?.investmentsBalance,
        employeeAvailableAmount: matchedEmployee?.availableAmount,
        employeeSsn: matchedEmployee?.ssn,
        isEmployeeFromSearch: false,
      });
    }
  }, [setEditableLockbox, matchedEmployees]);

  return (
    <Box>
      <Inscription color="textSecondary" margin={{ bottom: 'spacing8' }}>Suggested:</Inscription>

      <EmployeesList
        fields={fields}
        selectedEmployeeId={editableLockbox?.employeeId}
        onSelectEmployee={handleSelectEmployeeId}
        isEditMode={isEditMode}
        showWarnings
        emptyState={(
          <Box margin={{ top: 'spacing8' }}>
            <MatchingListEmptyState testId="lockbox-employees" title="No Suggestions" />
          </Box>
        )}
      />
    </Box>
  );
};

export default SuggestedEmployees;
